import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PdfColor,
  PdfPen,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { moduleRegistry } from '../../modules';
import { tableModule } from '../../modules/table';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

const COL1_X = MARGIN;
const COL2_X = MM(108);
const TABLE_W = MM(90);
const TABLE_H = MM(50);

const ROW1_Y = MM(20);
const ROW2_Y = MM(85);
const ROW3_Y = MM(150);

const R1_Y = MM(20);
const R2_Y = MM(85);
const R3_Y = MM(150);
const R4_Y = MM(215);

export class TestTable implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    moduleRegistry.register(tableModule);

    const font = new PdfFont(PdfFont.FONT_NAME, 14);
    const smallFont = new PdfFont(PdfFont.FONT_NAME, 10);

    const table = moduleRegistry.get('table');
    if (!table) {
      throw new Error('Table module not found');
    }

    const rectOutline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
    const titleRect = new PdfRect(MARGIN, MM(5), MM(186), MM(8));

    const dc = (
      page: PageGen,
      x: number,
      y: number,
      w: number,
      h: number,
      label: string,
      data: string,
      options: Record<string, string>
    ) => {
      page.drawString(
        label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(x, y - MM(5), w, MM(5)),
        ETextAlign.CenterLeft
      );
      const r = new PdfRect(x, y, w, h);
      page.drawRectangle(rectOutline, null, r);
      table.draw(table.parse(data, options), r, ETextAlign.CenterLeft, { font, page });
    };

    const dcFull = (
      page: PageGen,
      y: number,
      label: string,
      data: string,
      options: Record<string, string>
    ) => {
      dc(page, MARGIN, y, MM(186), MM(60), label, data, options);
    };

    const smallTableH = MM(35);

    // ─── Page 1: Data Sources ─────────────────────────────────────────────────
    const page1 = pdf.addPage();
    page1.drawString(
      'Table Module Tests - Data Sources',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(
      page1,
      COL1_X,
      R1_Y,
      TABLE_W,
      smallTableH,
      '1. JSON object array:',
      JSON.stringify({
        headers: ['Name', 'Score'],
        rows: [
          { Name: 'Alice', Score: 95 },
          { Name: 'Bob', Score: 87 },
          { Name: 'Carol', Score: 92 },
        ],
      }),
      {}
    );

    dc(
      page1,
      COL2_X,
      R1_Y,
      TABLE_W,
      smallTableH,
      '2. JSON array of arrays:',
      JSON.stringify({
        headers: ['Item', 'Qty'],
        rows: [
          ['Apples', 10],
          ['Bananas', 20],
          ['Oranges', 15],
        ],
      }),
      {}
    );

    dc(page1, COL1_X, R2_Y, TABLE_W, smallTableH, '3. JSON via data attr:', '', {
      data: JSON.stringify({
        headers: ['A', 'B'],
        rows: [
          [1, 2],
          [3, 4],
        ],
      }),
    });

    dc(page1, COL2_X, R2_Y, TABLE_W, smallTableH, '4. JSON via data-source:', '', {
      'data-source': JSON.stringify({
        headers: ['X', 'Y'],
        rows: [
          ['a', 'b'],
          ['c', 'd'],
        ],
      }),
    });

    dc(
      page1,
      COL1_X,
      R3_Y,
      TABLE_W,
      smallTableH,
      '5. Pipe-delimited:',
      `| Name | Age |
| ---- | --- |
| Dave | 25 |
| Eve | 30 |`,
      {}
    );

    dc(
      page1,
      COL2_X,
      R3_Y,
      TABLE_W,
      smallTableH,
      '6. Tab-delimited (CSV):',
      `Name,Age
Frank,28
Grace,35`,
      {}
    );

    // ─── Page 2: Styles ────────────────────────────────────────────────────────
    const page2 = pdf.addPage();
    page2.drawString(
      'Table Module Tests - Styles',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const styleData = `| Product | Price |
| ------- | ----- |
| Apple   |   1.5 |
| Banana   |   0.8 |
| Cherry   |   2.5 |`;

    dc(page2, COL1_X, R1_Y, TABLE_W, smallTableH, '7. Simple (no borders):', styleData, {
      style: 'simple',
    });

    dc(page2, COL2_X, R1_Y, TABLE_W, smallTableH, '8. Bordered:', styleData, { style: 'bordered' });

    dc(page2, COL1_X, R2_Y, TABLE_W, smallTableH, '9. Striped:', styleData, { style: 'striped' });

    dc(page2, COL2_X, R2_Y, TABLE_W, smallTableH, '10. Grid:', styleData, { style: 'grid' });

    // ─── Page 3: Layout Options ────────────────────────────────────────────────
    const page3 = pdf.addPage();
    page3.drawString(
      'Table Module Tests - Layout Options',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const layoutData = `| Short | Medium | Longer |
| ----- | ------ | ------ |
| A     | B      | C      |
| D     | E      | F      |`;

    dc(page3, COL1_X, R1_Y, TABLE_W, smallTableH, '11. Cols 20%,30%,50%:', layoutData, {
      cols: '20%,30%,50%',
    });

    dc(page3, COL2_X, R1_Y, TABLE_W, smallTableH, '12. Cols 40,80,60:', layoutData, {
      cols: '40,80,60',
    });

    const alignData = `| Left | Center | Right |
| ---- | ------ | ----- |
| A    | B      |     C |
| D    | E      |     F |`;

    dc(page3, COL1_X, R2_Y, TABLE_W, smallTableH, '13. Align left:', alignData, { align: 'left' });

    dc(page3, COL2_X, R2_Y, TABLE_W, smallTableH, '14. Align center:', alignData, {
      align: 'center',
    });

    dc(page3, COL1_X, R3_Y, TABLE_W, smallTableH, '15. Align right:', alignData, {
      align: 'right',
    });

    dc(page3, COL2_X, R3_Y, TABLE_W, smallTableH, '16. Mixed align:', alignData, {
      align: 'left,center,right',
    });

    // ─── Page 4: Header/Size Options ───────────────────────────────────────────
    const page4 = pdf.addPage();
    page4.drawString(
      'Table Module Tests - Header & Size Options',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const sizeData = `| Col A | Col B |
| ----- | ----- |
| val   | data  |
| more  | info  |`;

    dc(page4, COL1_X, R1_Y, TABLE_W, smallTableH, '17. Header height 30:', sizeData, {
      'header-height': '30',
    });

    dc(page4, COL2_X, R1_Y, TABLE_W, smallTableH, '18. Row min-height 25:', sizeData, {
      'min-row-height': '25',
    });

    dc(page4, COL1_X, R2_Y, TABLE_W, smallTableH, '19. Cell padding 5:', sizeData, {
      'cell-padding': '5',
    });

    dc(page4, COL2_X, R2_Y, TABLE_W, smallTableH, '20. Repeat header true:', sizeData, {
      'repeat-header': 'true',
    });

    dc(page4, COL1_X, R3_Y, TABLE_W, smallTableH, '21. Repeat header false:', sizeData, {
      'repeat-header': 'false',
      style: 'bordered',
    });

    // ─── Page 5: Footer Calculations ─────────────────────────────────────────────
    const page5 = pdf.addPage();
    page5.drawString(
      'Table Module Tests - Footer Calculations',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const numericData = `| Item    | Qty | Price |
| ------- | --- | ----- |
| Apple   |  10 |   150 |
| Banana  |  20 |   160 |
| Cherry  |  15 |   225 |`;

    dc(page5, COL1_X, R1_Y, TABLE_W, smallTableH, '22. Footer sum:', numericData, {
      footer: 'sum',
    });

    dc(page5, COL2_X, R1_Y, TABLE_W, smallTableH, '23. Footer avg:', numericData, {
      footer: 'avg',
    });

    dc(page5, COL1_X, R2_Y, TABLE_W, smallTableH, '24. Footer count:', numericData, {
      footer: 'count',
    });

    dc(page5, COL2_X, R2_Y, TABLE_W, smallTableH, '25. Custom JS (max):', numericData, {
      footer: 'custom',
      'footer-fn': 'Math.max(...values)',
    });

    // Footer with style
    dc(page5, COL1_X, R3_Y, TABLE_W, smallTableH, '26. Sum + striped:', numericData, {
      footer: 'sum',
      style: 'striped',
    });

    dc(page5, COL2_X, R3_Y, TABLE_W, smallTableH, '27. Avg + grid:', numericData, {
      footer: 'avg',
      style: 'grid',
    });

    // ─── Page 6: Multi-Page Long Tables ────────────────────────────────────────
    const page6 = pdf.addPage();
    page6.drawString(
      'Table Module Tests - Multi-Page Tables',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const generateManyRows = (count: number) => {
      const rows = [];
      for (let i = 1; i <= count; i++) {
        rows.push({ Item: `Item ${i}`, Value: i * 10, Note: `Note for item ${i}` });
      }
      return JSON.stringify({
        headers: ['Item', 'Value', 'Note'],
        rows,
      });
    };

    dc(
      page6,
      COL1_X,
      R1_Y,
      TABLE_W,
      smallTableH,
      '28. Long table (50 rows):',
      generateManyRows(50),
      { style: 'striped' }
    );

    dc(page6, COL2_X, R1_Y, TABLE_W, smallTableH, '29. Long + sum footer:', generateManyRows(50), {
      footer: 'sum',
      style: 'grid',
    });

    const generateMoreRows = (count: number) => {
      const rows = [];
      for (let i = 1; i <= count; i++) {
        rows.push({ ID: i, Name: `Student ${i}`, Grade: Math.floor(Math.random() * 40 + 60) });
      }
      return JSON.stringify({
        headers: ['ID', 'Name', 'Grade'],
        rows,
      });
    };

    dc(page6, COL1_X, R2_Y, TABLE_W, smallTableH, '30. 80 students + avg:', generateMoreRows(80), {
      footer: 'avg',
    });

    dc(page6, COL2_X, R2_Y, TABLE_W, smallTableH, '31. 100 rows + count:', generateMoreRows(100), {
      footer: 'count',
      style: 'striped',
    });

    // ─── Page 7: Edge Cases ─────────────────────────────────────────────────────
    const page7 = pdf.addPage();
    page7.drawString(
      'Table Module Tests - Edge Cases',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    dc(page7, COL1_X, R1_Y, TABLE_W, smallTableH, '32. Empty data:', '{}', {});

    dc(
      page7,
      COL2_X,
      R1_Y,
      TABLE_W,
      smallTableH,
      '33. Single row:',
      `| Col |
| --- |
| A   |`,
      {}
    );

    dc(
      page7,
      COL1_X,
      R2_Y,
      TABLE_W,
      smallTableH,
      '34. Single column:',
      `| Items |
| ------ |
| One   |
| Two   |
| Three |`,
      {}
    );

    dc(
      page7,
      COL2_X,
      R2_Y,
      TABLE_W,
      smallTableH,
      '35. Long cell text:',
      `| Description |
| ----------- |
| This is a very long text that should wrap to multiple lines within the cell |
| Short |`,
      {}
    );

    dc(
      page7,
      COL1_X,
      R3_Y,
      TABLE_W,
      smallTableH,
      '36. Special chars:',
      `| Symbol | Value |
| ------ | ----- |
| $100   | 100   |
| 50%    | 0.5   |
| &more  | test  |`,
      {}
    );

    dc(
      page7,
      COL2_X,
      R3_Y,
      TABLE_W,
      smallTableH,
      '37. Unicode:',
      `| Language | native |
| -------- | ------- |
| Čeština |    Dobrý |
| 日本語   |    こんにちは |
| Español |    Hola   |`,
      {}
    );

    // ─── Page 8: Real-World Examples ─────────────────────────────────────────
    const page8 = pdf.addPage();
    page8.drawString(
      'Table Module Tests - Real-World Examples',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const gradebookData = JSON.stringify({
      headers: ['Student', 'Math', 'English', 'Science', 'History'],
      rows: [
        { Student: 'Alice', Math: 85, English: 92, Science: 88, History: 78 },
        { Student: 'Bob', Math: 72, English: 81, Science: 90, History: 85 },
        { Student: 'Carol', Math: 95, English: 88, Science: 92, History: 80 },
        { Student: 'David', Math: 68, English: 75, Science: 72, History: 90 },
        { Student: 'Eve', Math: 90, English: 95, Science: 85, History: 88 },
      ],
    });

    dcFull(page8, R1_Y, '38. Gradebook with averages:', gradebookData, {
      footer: 'avg',
      style: 'striped',
    });

    const inventoryData = JSON.stringify({
      headers: ['SKU', 'Product', 'Stock', 'Unit Price'],
      rows: [
        { SKU: 'A001', Product: 'Widget', Stock: 150, 'Unit Price': 9.99 },
        { SKU: 'A002', Product: 'Gadget', Stock: 75, 'Unit Price': 24.99 },
        { SKU: 'B001', Product: 'Gizmo', Stock: 200, 'Unit Price': 5.5 },
        { SKU: 'B002', Product: 'Thingamajig', Stock: 50, 'Unit Price': 15.0 },
      ],
    });

    dcFull(page8, R2_Y, '39. Inventory with count:', inventoryData, {
      footer: 'count',
      style: 'grid',
    });

    const financialData = JSON.stringify({
      headers: ['Category', 'Q1', 'Q2', 'Q3', 'Q4'],
      rows: [
        { Category: 'Revenue', Q1: 50000, Q2: 55000, Q3: 62000, Q4: 70000 },
        { Category: 'COGS', Q1: 20000, Q2: 22000, Q3: 25000, Q4: 28000 },
        { Category: 'Marketing', Q1: 5000, Q2: 6000, Q3: 5500, Q4: 7000 },
        { Category: 'Operations', Q1: 8000, Q2: 8500, Q3: 9000, Q4: 9500 },
      ],
    });

    dcFull(page8, R3_Y, '40. Financial report with sum:', financialData, {
      footer: 'sum',
      style: 'bordered',
    });

    // ─── Page 9: Very Long Table (Full Stress Test) ─────────────────────────
    const page9 = pdf.addPage();
    page9.drawString(
      'Table Module Tests - Stress Test',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const stressData = (rowCount: number) => {
      const rows = [];
      for (let i = 1; i <= rowCount; i++) {
        rows.push({
          ID: i,
          Name: `Item ${i}`,
          Description: `Description for item ${i} with some extra text`,
          Value: Math.floor(Math.random() * 1000),
          Status: ['Active', 'Pending', 'Completed'][i % 3],
        });
      }
      return JSON.stringify({
        headers: ['ID', 'Name', 'Description', 'Value', 'Status'],
        rows,
      });
    };

    dcFull(page9, R1_Y, '41. Very long table (150 rows) + sum:', stressData(150), {
      footer: 'sum',
      style: 'striped',
    });

    // ─── Page 10: Combined Options ────────────────────────────────────────────
    const page10 = pdf.addPage();
    page10.drawString(
      'Table Module Tests - Combined Options',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const combinedData = JSON.stringify({
      headers: ['Region', 'Sales', 'Target', 'Actual'],
      rows: [
        { Region: 'North', Sales: 120, Target: 100, Actual: 125 },
        { Region: 'South', Sales: 85, Target: 90, Actual: 82 },
        { Region: 'East', Sales: 110, Target: 105, Actual: 115 },
        { Region: 'West', Sales: 95, Target: 100, Actual: 88 },
      ],
    });

    dc(page10, COL1_X, R1_Y, TABLE_W, smallTableH, '42. Full options:', combinedData, {
      style: 'grid',
      cols: '30%,20%,20%,20%',
      align: 'left,center,center,right',
      'header-height': '25',
      'min-row-height': '22',
      footer: 'sum',
    });

    dc(page10, COL2_X, R1_Y, TABLE_W, smallTableH, '43. No header repeat:', combinedData, {
      style: 'striped',
      'repeat-header': 'false',
      footer: 'avg',
    });

    const narrowData = JSON.stringify({
      headers: ['Code', 'Count'],
      rows: [
        { Code: 'A', Count: 10 },
        { Code: 'B', Count: 25 },
        { Code: 'C', Count: 15 },
      ],
    });

    dc(page10, COL1_X, R2_Y, MM(50), smallTableH, '44. Narrow table:', narrowData, {
      style: 'grid',
      footer: 'sum',
    });

    dc(page10, COL2_X, R2_Y, MM(50), smallTableH, '45. Narrow + avg:', narrowData, {
      style: 'striped',
      footer: 'avg',
    });

    // Summary
    page10.drawString(
      'Test complete: 45 test cases covering data sources, styles, layouts, sizes, footers, multi-page, edge cases, and real examples.',
      smallFont,
      PdfBrush.LightGray,
      null,
      new PdfRect(MARGIN, R4_Y, MM(186), MM(10)),
      ETextAlign.CenterLeft
    );
  }
}
